/**
 * Move → harmonic function.
 *
 * Fieldbook §6 specifies the palette's active tag as "2px ink border + wash
 * tint of the move's dominant function color". This is that mapping.
 *
 * PROPOSED 2026-09-01, owner to confirm — see the "Move → harmonic function"
 * section of docs/design/MOVES-SPEC.md for the rule and the per-row theory.
 *
 * The rule in one line: the tint names what the move does to harmony. A move
 * that produces or targets a single diatonic function takes that function's
 * wash; a move whose defining act is borrowing takes the borrowed plum; a move
 * that changes texture, voicing or spacing without changing function gets no
 * tint at all. Colour is never decorative here.
 */

/** The four Fieldbook function washes, plus "no function" for texture moves. */
export type MoveFunction = 'tonic' | 'predominant' | 'dominant' | 'borrowed' | null;

/**
 * The CSS custom property each function paints with. `null` means the tag
 * carries no wash — the active state is the 2px ink border alone.
 */
export const FUNCTION_WASH: Record<Exclude<MoveFunction, null>, string> = {
  tonic: 'var(--wash-tonic)',
  predominant: 'var(--wash-motion)',
  dominant: 'var(--wash-tension)',
  borrowed: 'var(--wash-borrowed)',
};

/**
 * Keyed by Move.id. Every move in MOVES must appear here exactly once — the
 * test in __tests__/function-map.test.ts fails if a move is added without a
 * function decision, so the mapping cannot silently fall out of date.
 *
 * `null` is a decision, not a gap: it records that the move does not change
 * harmonic function, or that its function is genuinely the user's choice
 * rather than the move's.
 */
export const MOVE_FUNCTIONS: Record<string, MoveFunction> = {
  // Diatonic function.
  'delay-the-arrival': 'tonic',
  'brighten-the-ending': 'tonic',
  'take-the-long-way-home': 'dominant',

  // Borrowing is the defining act; no one diatonic function covers the result.
  'darken-it': 'borrowed',
  sidestep: 'borrowed',

  // Function is preserved or is the user's choice — a tint would overclaim.
  'suspend-into-it': null,
  'let-the-bass-hold': null,

  // Texture and colour, not function.
  'walk-the-bass': null,
  'widen-it': null,
  'thin-it': null,
};

/** The wash a move's active tag paints with, or null for no tint. */
export function washForMove(moveId: string): string | null {
  const fn = MOVE_FUNCTIONS[moveId];
  return fn ? FUNCTION_WASH[fn] : null;
}
